/**
 * PitchPrint Centralized State Manager
 * Single source of truth for all PitchPrint state across the site
 * Version: 1.0.0
 */
window.PitchPrintStateManager = (() => {
  'use strict';

  // ⚠️ CRITICAL: Do NOT run on main cart page to avoid conflicts
  if (window.location.pathname === '/cart') {
    console.log('[StateManager] 🚫 Skipping on main cart page to avoid PitchPrint conflicts');
    // Return minimal API to prevent errors in dependent scripts
    return {
      on: () => {},
      emit: () => {},
      getState: () => ({}),
      getProjectId: () => null,
      getVariantData: () => null,
      isVariantLocked: () => false,
      isSystemReady: () => false,
      isToastActive: () => false,
      isHardwareOnly: () => false,
      setProject: () => {},
      clearProject: () => {},
      lockVariants: () => {},
      unlockVariants: () => {},
      setSystemReady: () => {},
      setToastActive: () => {},
      setToastInactive: () => {},
      setCartPreview: () => {},
      getCartPreview: () => null,
      addError: () => {},
      clearErrors: () => {},
      checkIfHardwareOnly: () => false,
      checkVariantOptions: () => false,
      setHardwareOnly: () => {},
      getHardwareOnlyKeywords: () => [],
      // Quick Design flow stubs (v4.2.0)
      setQuickDesignStep: () => {},
      getQuickDesignStep: () => null,
      setQuickDesignFrontSelected: () => {},
      setQuickDesignBackSelected: () => {},
      setQuickDesignDoubleSided: () => {},
      recalculateQuickDesignStep: () => {},
      resetQuickDesignFlow: () => {}
    };
  }

  // Debug mode toggle - can be enabled via console
  const DEBUG = window.PITCHPRINT_DEBUG || false;
  
  // Hardware-only variant keywords - variants with these option values don't require customization
  const HARDWARE_ONLY_KEYWORDS = [
    'Pole Only (No Flag)',
    'Stand Only (No Graphic)',
    'Frame Only (No Fabric)'
  ];

  // Central state object
  let state = {
    projectId: null,
    variantData: null,
    variantLocked: false,
    systemReady: false,
    cartPreviews: new Map(),
    toastActive: false,
    errors: [],
    // Project metadata
    numPages: null,
    shape: null,
    previewUrl: null, // Direct preview URL (from design radio templates or other sources)
    designId: null,   // PitchPrint design ID (from design radio templates)
    // File upload state
    uploadMode: null, // 'upload_now' | 'send_later' | null
    uploadData: null, // { uploadId, files: { front, back }, previews: { front, back }, shape, sides }
    uploadActive: false,
    // Design method state (for toggle)
    designMethod: null, // 'file_upload' | 'pitchprint' | null
    // Design path state (for Quick vs Custom toggle)
    designPath: null, // 'quick' | 'custom' | null
    // Hardware-only variant state
    isHardwareOnly: false, // true when current variant is hardware-only (no customization required)
    // Quick Design flow state (v4.2.0)
    quickDesignStep: null,           // 'choose-front' | 'choose-back' | 'preview' | 'ready' | null
    quickDesignFrontSelected: false,
    quickDesignBackSelected: false,
    quickDesignIsDoubleSided: false
  };

  // Event system for cross-component communication
  const events = new EventTarget();
  
  // Debug logging helper
  const log = (...args) => DEBUG && console.log('[StateManager]', ...args);

  // Public API
  const StateManager = {
    // =====================================
    // STATE GETTERS
    // =====================================

    getState: () => ({ ...state }),
    getProjectId: () => state.projectId,
    getVariantData: () => state.variantData,
    isVariantLocked: () => state.variantLocked,
    isSystemReady: () => state.systemReady,
    isToastActive: () => state.toastActive,
    getUploadMode: () => state.uploadMode,
    getUploadData: () => state.uploadData,
    isUploadActive: () => state.uploadActive,
    getDesignMethod: () => state.designMethod,
    getDesignPath: () => state.designPath,
    isHardwareOnly: () => state.isHardwareOnly,
    getHardwareOnlyKeywords: () => [...HARDWARE_ONLY_KEYWORDS],
    getPreviewUrl: () => state.previewUrl,
    getDesignId: () => state.designId,

    // =====================================
    // PROJECT MANAGEMENT
    // =====================================
    
    setProject(projectData) {
      const oldProjectId = state.projectId;
      state.projectId = projectData.projectId;
      state.variantData = projectData.variant;

      // Store project metadata for toast preview and other systems
      if (projectData.numPages !== undefined) {
        state.numPages = projectData.numPages;
      }
      if (projectData.shape) {
        state.shape = projectData.shape;
      }
      // Store direct preview URL (from design radio templates)
      if (projectData.previewUrl) {
        state.previewUrl = projectData.previewUrl;
      }
      // Store design ID (from design radio templates)
      if (projectData.designId) {
        state.designId = projectData.designId;
      }

      log('Project set:', projectData);

      if (oldProjectId !== projectData.projectId) {
        this.emit('project:changed', projectData);
      }

      if (projectData.projectId) {
        this.lockVariants();
        this.emit('project:restored', projectData);

        // Update Quick Design step to 'ready' when project is saved
        if (state.designPath === 'quick') {
          this.setQuickDesignStep('ready');
        }
      }
    },

    clearProject() {
      const hadProject = !!state.projectId;
      state.projectId = null;
      state.variantData = null;
      state.numPages = null;
      state.shape = null;
      state.previewUrl = null;
      state.designId = null;

      log('Project cleared');

      if (hadProject) {
        this.unlockVariants();
        this.emit('project:cleared');
      }
    },

    // =====================================
    // VARIANT CONTROL
    // =====================================
    
    lockVariants() {
      if (!state.variantLocked) {
        state.variantLocked = true;
        log('Variants locked');
        this.emit('variants:locked');
      }
    },

    unlockVariants() {
      if (state.variantLocked) {
        state.variantLocked = false;
        log('Variants unlocked');
        this.emit('variants:unlocked');
      }
    },

    // =====================================
    // SYSTEM STATUS
    // =====================================
    
    setSystemReady() {
      if (!state.systemReady) {
        state.systemReady = true;
        log('System ready');
        this.emit('system:ready');
      }
    },

    // =====================================
    // CART PREVIEW MANAGEMENT
    // =====================================
    
    setCartPreview(lineId, previewData) {
      state.cartPreviews.set(lineId, previewData);
      log('Cart preview set for line:', lineId);
      this.emit('cart:preview:updated', { lineId, previewData });
    },

    getCartPreview(lineId) {
      return state.cartPreviews.get(lineId);
    },

    clearCartPreviews() {
      state.cartPreviews.clear();
      log('All cart previews cleared');
      this.emit('cart:previews:cleared');
    },

    // =====================================
    // TOAST WIDGET MANAGEMENT
    // =====================================
    
    activateToast(projectData) {
      if (!state.toastActive) {
        state.toastActive = true;
        log('Toast activated');
        this.emit('toast:activate', projectData);
      }
    },

    deactivateToast() {
      if (state.toastActive) {
        state.toastActive = false;
        log('Toast deactivated');
        this.emit('toast:deactivate');
      }
    },

    // =====================================
    // UPLOAD MANAGEMENT
    // =====================================

    setUploadMode(mode) {
      state.uploadMode = mode;
      log('Upload mode set:', mode);
      this.emit('upload:mode:changed', mode);
    },

    setUploadData(uploadData) {
      state.uploadData = uploadData;
      state.uploadActive = !!uploadData;
      log('Upload data set:', uploadData);
      this.emit('upload:data:updated', uploadData);

      if (uploadData) {
        this.lockVariants();
        this.emit('upload:completed', uploadData);
      }
    },

    clearUploadData() {
      const hadUpload = !!state.uploadData;
      state.uploadData = null;
      state.uploadMode = null;
      state.uploadActive = false;

      log('Upload data cleared');

      if (hadUpload) {
        this.unlockVariants();
        this.emit('upload:cleared');
      }
    },

    // =====================================
    // DESIGN METHOD MANAGEMENT
    // =====================================

    setDesignMethod(method) {
      const oldMethod = state.designMethod;

      if (oldMethod === method) {
        log('Design method already set to:', method);
        return;
      }

      state.designMethod = method;
      log('Design method changed:', oldMethod, '→', method);
      this.emit('design:method:changed', method);

      // Auto-set based on what data is active
      if (method === 'pitchprint' && state.projectId) {
        log('PitchPrint method selected with active project');
      } else if (method === 'file_upload' && state.uploadData) {
        log('File upload method selected with active upload');
      }
    },

    clearDesignMethod() {
      state.designMethod = null;
      log('Design method cleared');
    },

    // =====================================
    // DESIGN PATH MANAGEMENT (Quick vs Custom)
    // =====================================

    setDesignPath(path) {
      const oldPath = state.designPath;

      if (oldPath === path) {
        log('Design path already set to:', path);
        return;
      }

      state.designPath = path;
      log('Design path changed:', oldPath, '→', path);
      this.emit('design:path:changed', path);

      // Activate/deactivate Quick Design flow based on path
      if (path === 'quick') {
        this.recalculateQuickDesignStep();
      } else {
        this.resetQuickDesignFlow();
      }
    },

    clearDesignPath() {
      state.designPath = null;
      log('Design path cleared');
    },

    // =====================================
    // QUICK DESIGN FLOW MANAGEMENT (v4.2.0)
    // =====================================

    /**
     * Set the current Quick Design step
     * @param {string} step - 'choose-front' | 'choose-back' | 'preview' | 'ready'
     */
    setQuickDesignStep(step) {
      const oldStep = state.quickDesignStep;
      if (oldStep === step) return;

      state.quickDesignStep = step;
      log('Quick design step changed:', oldStep, '→', step);
      this.emit('quickdesign:step:changed', {
        step,
        frontSelected: state.quickDesignFrontSelected,
        backSelected: state.quickDesignBackSelected,
        isDoubleSided: state.quickDesignIsDoubleSided
      });
    },

    getQuickDesignStep() {
      return state.quickDesignStep;
    },

    /**
     * Set front design selection state
     * @param {boolean} selected - Whether front design is selected
     */
    setQuickDesignFrontSelected(selected) {
      state.quickDesignFrontSelected = selected;
      log('Quick design front selected:', selected);
      this.recalculateQuickDesignStep();
    },

    /**
     * Set back design selection state
     * @param {boolean} selected - Whether back design is selected
     */
    setQuickDesignBackSelected(selected) {
      state.quickDesignBackSelected = selected;
      log('Quick design back selected:', selected);
      this.recalculateQuickDesignStep();
    },

    /**
     * Set whether product is double-sided
     * @param {boolean} isDoubleSided - Whether product has double-sided variant
     */
    setQuickDesignDoubleSided(isDoubleSided) {
      const wasDoubleSided = state.quickDesignIsDoubleSided;
      state.quickDesignIsDoubleSided = isDoubleSided;

      if (wasDoubleSided !== isDoubleSided) {
        log('Quick design double-sided changed:', wasDoubleSided, '→', isDoubleSided);
        this.recalculateQuickDesignStep();
      }
    },

    /**
     * Recalculate the current Quick Design step based on state
     * Called automatically when front/back selection or double-sided status changes
     */
    recalculateQuickDesignStep() {
      if (state.designPath !== 'quick') return;

      // Check if project already saved
      if (state.projectId) {
        this.setQuickDesignStep('ready');
        return;
      }

      if (!state.quickDesignFrontSelected) {
        this.setQuickDesignStep('choose-front');
      } else if (state.quickDesignIsDoubleSided && !state.quickDesignBackSelected) {
        this.setQuickDesignStep('choose-back');
      } else {
        this.setQuickDesignStep('preview');
      }
    },

    /**
     * Reset the Quick Design flow state
     * Called when switching away from Quick Design path
     */
    resetQuickDesignFlow() {
      state.quickDesignStep = null;
      state.quickDesignFrontSelected = false;
      state.quickDesignBackSelected = false;
      // Note: Don't reset quickDesignIsDoubleSided - that's controlled by variant selection
      log('Quick design flow reset');
    },

    // =====================================
    // HARDWARE-ONLY VARIANT MANAGEMENT
    // =====================================

    /**
     * Check if a variant option value indicates hardware-only (no customization required)
     * @param {string} optionValue - The variant option value to check
     * @returns {boolean} True if the option indicates hardware-only
     */
    checkIfHardwareOnly(optionValue) {
      if (!optionValue) return false;
      return HARDWARE_ONLY_KEYWORDS.some(keyword =>
        optionValue.toLowerCase().includes(keyword.toLowerCase())
      );
    },

    /**
     * Check all current variant options and determine if it's hardware-only
     * @param {Array<string>} optionValues - Array of current variant option values
     * @returns {boolean} True if any option indicates hardware-only
     */
    checkVariantOptions(optionValues) {
      if (!optionValues || !Array.isArray(optionValues)) return false;
      return optionValues.some(value => this.checkIfHardwareOnly(value));
    },

    /**
     * Set the hardware-only state and emit event if changed
     * @param {boolean} isHardwareOnly - Whether the current variant is hardware-only
     */
    setHardwareOnly(isHardwareOnly) {
      const wasHardwareOnly = state.isHardwareOnly;
      state.isHardwareOnly = isHardwareOnly;

      if (wasHardwareOnly !== isHardwareOnly) {
        log('Hardware-only state changed:', wasHardwareOnly, '→', isHardwareOnly);
        this.emit('variant:hardware-only:changed', { isHardwareOnly });

        // If switching to hardware-only, unlock variants and clear customization state
        if (isHardwareOnly) {
          this.unlockVariants();
          log('Customization disabled for hardware-only variant');
        }
      }
    },

    // =====================================
    // ERROR HANDLING
    // =====================================
    
    logError(error, context) {
      const errorEntry = { 
        error: error.toString(), 
        context, 
        timestamp: Date.now(),
        stack: error.stack || 'No stack trace'
      };
      
      state.errors.push(errorEntry);
      
      // Keep only last 10 errors to prevent memory bloat
      if (state.errors.length > 10) {
        state.errors.shift();
      }
      
      console.log('[PitchPrint Error]', context, error);
      this.emit('error:logged', errorEntry);
    },

    // =====================================
    // EVENT SYSTEM
    // =====================================
    
    on(event, callback) {
      events.addEventListener(event, callback);
      log('Event listener added:', event);
    },

    off(event, callback) {
      events.removeEventListener(event, callback);
      log('Event listener removed:', event);
    },

    emit(event, data) {
      log('Emitting event:', event, data);
      events.dispatchEvent(new CustomEvent(event, { detail: data }));
    },

    // =====================================
    // DEBUG TOOLS
    // =====================================
    
    debug: {
      enableDebug() {
        window.PITCHPRINT_DEBUG = true;
        console.log('🐛 PitchPrint Debug Mode: ENABLED');
        console.log('Available commands:');
        console.log('- PitchPrintStateManager.debug.validateState()');
        console.log('- PitchPrintStateManager.debug.simulateUserScenario()');
        console.log('- PitchPrintStateManager.debug.getErrors()');
        console.log('- togglePitchPrintDebug()');
      },

      disableDebug() {
        window.PITCHPRINT_DEBUG = false;
        console.log('🐛 PitchPrint Debug Mode: DISABLED');
      },

      getErrors() {
        return state.errors;
      },

      getState() {
        return state;
      },

      reset() {
        const oldState = { ...state };
        state = {
          projectId: null,
          variantData: null,
          variantLocked: false,
          systemReady: false,
          cartPreviews: new Map(),
          toastActive: false,
          errors: []
        };
        console.log('🔄 State reset:', { oldState, newState: state });
        StateManager.emit('debug:state:reset');
      },

      // Test the critical user scenario
      simulateUserScenario() {
        console.log('🧪 Testing user scenario: save → navigate away → return');
        
        // Step 1: Simulate project save
        const testProjectData = {
          projectId: 'test123456789abcdef',
          variant: { 
            id: 'variant123', 
            options: ['Red', 'Large'] 
          }
        };
        
        console.log('1️⃣ Simulating project save...');
        StateManager.setProject(testProjectData);
        
        // Step 2: Simulate navigation away (state should persist)
        setTimeout(() => {
          console.log('2️⃣ Simulating page navigation away (state persists)...');
          console.log('   Current project ID:', StateManager.getProjectId());
          console.log('   Variants locked:', StateManager.isVariantLocked());
        }, 1000);
        
        // Step 3: Simulate return to page
        setTimeout(() => {
          console.log('3️⃣ Simulating return to page...');
          console.log('   Should trigger: project restoration, variant locking, toast activation');
          StateManager.emit('project:restored', testProjectData);
          console.log('✅ User scenario test complete');
        }, 2000);
      },

      // Validate current state and log summary
      validateState() {
        const currentState = StateManager.getState();
        console.table({
          'Project ID': currentState.projectId || 'None',
          'Variant Locked': currentState.variantLocked,
          'System Ready': currentState.systemReady,
          'Toast Active': currentState.toastActive,
          'Cart Previews': currentState.cartPreviews.size,
          'Total Errors': currentState.errors.length
        });
        
        if (currentState.errors.length > 0) {
          console.log('Recent errors:');
          currentState.errors.slice(-3).forEach((error, index) => {
            console.log(`${index + 1}. [${error.context}] ${error.error}`);
          });
        }
        
        return currentState;
      },

      // Performance monitoring
      getPerformanceMetrics() {
        return {
          memoryUsage: performance.memory ? {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + ' MB',
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024) + ' MB',
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
          } : 'Not available',
          timing: performance.timing ? {
            pageLoadTime: (performance.timing.loadEventEnd - performance.timing.navigationStart) + ' ms',
            domContentLoaded: (performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart) + ' ms'
          } : 'Not available',
          stateSize: JSON.stringify(state).length + ' bytes'
        };
      }
    }
  };

  // Initialize debug mode if enabled
  if (DEBUG) {
    StateManager.debug.enableDebug();
  }

  log('PitchPrint StateManager initialized');
  
  return StateManager;
})();

// =====================================
// GLOBAL DEBUG HELPER
// =====================================

// Easy debug toggle function accessible from console
window.togglePitchPrintDebug = () => {
  if (window.PITCHPRINT_DEBUG) {
    window.PitchPrintStateManager.debug.disableDebug();
  } else {
    window.PitchPrintStateManager.debug.enableDebug();
  }
};